import { CourseFromGroupApi, courseformsApi } from "api";
import { FaExclamationTriangle, FaLock } from "react-icons/fa";
import React, { useState } from "react";

import { TabContent } from "../TabLayout";

import { PaziaButton, PaziaLoader } from "components/atoms";
import {
  ImportFormGroupModal,
  ImportFormGroupValues,
} from "components/templates/ImportFormGroupModal/ImportFormGroupModal";
import { TermsList } from "./TermsList";
import { ApplicationFormGroupForm } from "./ApplicationFormGroupForm";
import { useToaster } from "utils";
import { AddApplicationFormGroupModal } from "components/templates/AddApplicationFormGroupModal/AddApplicationFormGroupModal";
import { ApplicationFormSchema } from "components/templates/AddApplicationFormGroupModal/ApplicationForm";
import { useCourseApplicationForm } from "api/course-forms";
import { PaziaBox } from "components/atoms";
import { AdminResourceDeleteModal, ToggleReadinessButton } from "components/molecules";
import { useCourse } from "api/hooks";
import { coursesStatusApi } from "api/coursesStatusApi";
import { useDeleteResourceActions } from "utils/store";

interface ApplicationFormStepProps {
  courseId: number;
}
export const ApplicationFormStep = ({ courseId }: ApplicationFormStepProps): JSX.Element | null => {
  const { data, loading, refresh } = useCourseApplicationForm(courseId);
  const { setDeleteResource } = useDeleteResourceActions();
  const { loading: loadingCourse, data: course, invalidate } = useCourse(courseId);
  const [activeGroup, setActiveGroup] = useState<CourseFromGroupApi | null>(null);
  const [modalGroup, setModalGroup] = useState<CourseFromGroupApi | null>(null);
  const { showSuccess, showError } = useToaster();

  if (loading || loadingCourse) return <PaziaLoader position="center" />;
  if (!data || !course) return null;

  const inheritedGroups = data.groups.filter((g) => g.isFromCategory);
  const ownGroups = data.groups.filter((g) => !g.isFromCategory);

  const onGroupSubmit = async (formData: ApplicationFormSchema) => {
    try {
      if (formData.id) {
        await courseformsApi.updateGroup(courseId, data.id, formData.id, formData);
        showSuccess("Grupa została zaktualizowana.");
      } else {
        await courseformsApi.addGroup(courseId, data.id, formData);
        showSuccess("Grupa została dodana.");
      }
      if (activeGroup) {
        refresh(async (updated) => {
          setActiveGroup(updated?.groups.find((x) => x.courseFormGroupId == activeGroup.courseFormGroupId) ?? null);
        });
      } else refresh();
      setModalGroup(null);
    } catch (e) {
      showError(formData.id ? "Nie udało się edytować grupy" : "Nie udało się dodać grupy");
    }
  };

  return (
    <TabContent className="flex flex-col gap-4">
      <PaziaBox className="col-span-10 flex justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Formularz zgłoszeniowy</h2>
          <div className="mb-2 text-sm">
            Pola formularza są widoczne dla kursantów przy zapisywaniu się na kurs.
            <br /> Kursant nie będzie mógł zapisać się na kurs z niezatwierdzonym formularzem. <br />
            Dodaj regulaminy, które kursant musi zaakceptować przed zapisaniem się na kurs.
          </div>
          <ToggleReadinessButton
            isReady={Boolean(course?.data.applicationFormReady)}
            onReady={coursesStatusApi.APPLICATION_FORM_READY}
            onUnready={coursesStatusApi.APPLICATION_FORM_UNREADY}
            courseId={course.data.id}
            readyText="Formularz został zatwierdzony"
            invalidate={invalidate}
          />
        </div>
      </PaziaBox>
      <PaziaBox>
        {!activeGroup && (
          <>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full table-auto border-collapse border border-gray-200 dark:border-slate-700">
                <thead>
                  <tr className="bg-gray-100 dark:bg-slate-900">
                    <th className="border border-gray-300 px-4 py-2 text-left dark:border-slate-700"></th>
                    <th className="border border-gray-300 px-4 py-2 text-left dark:border-slate-700">Nazwa</th>
                    <th className="border border-gray-300 px-4 py-2 text-left dark:border-slate-700">Pola</th>
                    <th className="border border-gray-300 px-4 py-2 text-left dark:border-slate-700">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="w-10 border border-gray-300 px-4 py-2 text-center dark:border-slate-700">
                      <FaLock title="Grupa podstawowa" />
                    </td>
                    <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">Dane podstawowe</td>
                    <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                      Email, Imię, Nazwisko, Telefon
                    </td>
                    <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                      <div className="flex gap-2">
                        <PaziaButton title="Edytuj" type="primary" disabled />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                      <FaLock title="Grupa podstawowa" />
                    </td>
                    <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">Dane rozliczeniowe</td>
                    <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                      Faktura VAT (tak/nie), NIP, Nabywca, Adres
                    </td>
                    <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                      <div className="flex gap-2">
                        <PaziaButton title="Edytuj" type="primary" disabled />
                      </div>
                    </td>
                  </tr>
                  {/* <tr>
                  <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                    <FaLock title="Grupa podstawowa" />
                  </td>
                  <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">Urząd pracy</td>
                  <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                    Czy posiadasz dofinansowanie z urzędu pracy?, Nazwa urzędu pracy
                  </td>
                  <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                    <div className="flex gap-2">
                      <PaziaButton title="Edytuj" type="primary" disabled />
                    </div>
                  </td>
                </tr> */}
                  {inheritedGroups.map((group) => (
                    <tr key={group.courseFormGroupId} className="hover:bg-gray-100 dark:hover:bg-slate-700">
                      <td className="w-10 border border-gray-300 px-4 py-2 text-center dark:border-slate-700">
                        <FaLock title="Grupa domyślna kategorii" />
                      </td>
                      <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">{group.name}</td>
                      <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                        {group.fields.length > 0 ? (
                          group.fields.map((field) => field.name).join(", ")
                        ) : (
                          <div className="flex items-center gap-1 text-sm font-semibold text-yellow-500">
                            <FaExclamationTriangle /> Brak pól w tej grupie
                          </div>
                        )}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                        <div className="flex gap-2">
                          <PaziaButton title="Edytuj w kategorii" type="primary" disabled />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {ownGroups.map((group) => (
                    <tr key={group.courseFormGroupId} className="hover:bg-gray-100 dark:hover:bg-slate-700">
                      <td className="border border-gray-300 px-4 py-2 dark:border-slate-700"></td>
                      <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">{group.name}</td>
                      <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                        {group.fields.length > 0 ? (
                          group.fields.map((field) => field.name).join(", ")
                        ) : (
                          <div className="flex items-center gap-1 text-sm font-semibold text-yellow-500">
                            <FaExclamationTriangle /> Brak pól w tej grupie
                          </div>
                        )}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                        <div className="flex gap-2">
                          <PaziaButton title="Edytuj" type="tertiary" onClick={() => setActiveGroup(group)} />
                          <PaziaButton
                            title="Usuń"
                            type="danger2"
                            onClick={() => setDeleteResource(group.courseFormGroupId)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <CopyGroupButton courseId={courseId} formId={data.id} onSuccessCopy={() => refresh()} />
              <PaziaButton title="Dodaj grupę" type="primary" onClick={() => setModalGroup({} as CourseFromGroupApi)} />
            </div>
          </>
        )}
        {activeGroup && (
          <ApplicationFormGroupForm
            group={activeGroup}
            courseId={courseId}
            changeActiveGroup={setActiveGroup}
            refreshGroups={refresh}
          />
        )}

        <AddApplicationFormGroupModal
          group={modalGroup}
          onCloseRequest={() => setModalGroup(null)}
          onSubmit={onGroupSubmit}
        />

        <AdminResourceDeleteModal
          title="Usuń grupę"
          description="Jesteś pewien że chcesz usunąć grupę z formularza kursu?"
          onSuccessDelete={() => refresh()}
          deleteAction={async (id) => {
            await courseformsApi.deleteGroup(courseId, data.id, id);
          }}
        />
      </PaziaBox>

      <TermsList courseId={courseId} categoryId={course.group.category.id} />
    </TabContent>
  );
};

const CopyGroupButton = ({
  courseId,
  formId,
  onSuccessCopy,
}: {
  courseId: number;
  formId: number;
  onSuccessCopy: () => void;
}) => {
  const [importModalOpen, setImportModalOpen] = React.useState(false);
  const { showError, showSuccess } = useToaster();

  const onSubmit = async (data: ImportFormGroupValues) => {
    try {
      await courseformsApi.copyGroup(courseId, formId, Number(data.formGroupId));
      setImportModalOpen(false);
      showSuccess("Grupa została zaimportowana.");
      onSuccessCopy();
    } catch (e) {
      console.error(e);
      showError("Nie udało się zaimportować grupy.");
    }
  };

  return (
    <div className="flex justify-end gap-2">
      <PaziaButton title="Importuj grupę" type="primary" onClick={() => setImportModalOpen(true)} />
      <ImportFormGroupModal
        isOpen={importModalOpen}
        onCloseRequest={() => setImportModalOpen(false)}
        onSubmit={onSubmit}
      />
    </div>
  );
};
